import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { CATS, CITIES, catPath, cityPath, homePath, contractorPath, blogPath, termsPath, privacyPath, cityDisplayName } from "@/i18n/slugs";

const footerCatKeys = [
  "toiture", "renovation", "chassis", "isolation", "salle-de-bain",
  "terrasse", "piscine", "chauffage", "electricite", "cuisine"
];

const footerCities = [
  { city: "Bruxelles", cat: "toiture" },
  { city: "Namur", cat: "renovation" },
  { city: "Charleroi", cat: "renovation" },
  { city: "Mons", cat: "toiture" },
  { city: "Arlon", cat: "isolation" },
  { city: "Nivelles", cat: "renovation" },
  { city: "Bastogne", cat: "chauffage" },
  { city: "Marche-en-Famenne", cat: "toiture" },
  { city: "Luxembourg-Ville", cat: "renovation" },
  { city: "Esch-sur-Alzette", cat: "renovation" },
  { city: "Differdange", cat: "toiture" },
  { city: "Liège", cat: "renovation" },
];

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { lang, T } = useLang();

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to={homePath(lang)} className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">DM</span>
              </div>
              <span className="font-heading font-bold text-xl">
                DevisMaison<span className="text-orange-500">.be</span>
              </span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {T.footer.tagline}
            </p>
            <div className="flex flex-col gap-3 text-sm text-slate-400">
              <a href="mailto:contact@devismaison.be" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                contact@devismaison.be
              </a>
              <a href="tel:+3261468332" className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                +32 61 46 83 32
              </a>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {T.footer.location}
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">{T.footer.services}</h4>
            <ul className="space-y-2">
              {footerCatKeys.map((key) => {
                const cat = CATS.find((c) => c.key === key);
                return (
                  <li key={key}>
                    <Link
                      to={catPath(key, lang)}
                      className="text-sm text-slate-400 hover:text-white transition-colors"
                    >
                      {T.footer.devisPrefix} {cat[lang].name.toLowerCase()}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Villes */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">{T.footer.cities}</h4>
            <ul className="space-y-2">
              {footerCities.map((item) => (
                <li key={item.city}>
                  <Link
                    to={cityPath(item.cat, item.city, lang)}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {T.footer.worksAt} {cityDisplayName(item.city, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Info */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">{T.footer.info}</h4>
            <ul className="space-y-2">
              <li>
                <Link to={blogPath(lang)} className="text-sm text-slate-400 hover:text-white transition-colors">
                  {T.nav.blog}
                </Link>
              </li>
              <li>
                <Link to={contractorPath(lang)} className="text-sm text-slate-400 hover:text-white transition-colors">
                  {T.footer.contractorSpace}
                </Link>
              </li>
              <li>
                <Link to="/admin/login" className="text-sm text-slate-400 hover:text-white transition-colors">
                  {T.footer.adminSpace}
                </Link>
              </li>
              <li>
                <Link to={termsPath(lang)} className="text-sm text-slate-400 hover:text-white transition-colors">
                  {T.footer.terms}
                </Link>
              </li>
              <li>
                <Link to={privacyPath(lang)} className="text-sm text-slate-400 hover:text-white transition-colors">
                  {T.footer.privacy}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            © {currentYear} DevisMaison.be. {T.footer.rights}
          </p>
          <p className="text-sm text-slate-500">
            {T.footer.region}
          </p>
        </div>
      </div>
    </footer>
  );
}
